import { useCallback, useEffect, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  Bold,
  Italic,
  List,
  Heading2,
  Undo,
  Redo,
  Underline as UnderlineIcon,
  FileText,
  Download,
  Link as LinkIcon,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import { exportDocumentPDF, exportDocumentDOCX } from '../lib/export'

interface LegalDocumentEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

export function LegalDocumentEditor({
  value,
  onChange,
  placeholder,
  className,
}: LegalDocumentEditorProps) {
  const { t } = useTranslation()
  const [isMounted, setIsMounted] = useState(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline underline-offset-2',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    ],
    content: value || '<p></p>',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[250px] px-4 py-3',
      },
      transformPastedText(text) {
        return text
      },
      transformPastedHTML(html) {
        // Strip all formatting, keep only plain text wrapped in paragraphs
        const temp = document.createElement('div')
        temp.innerHTML = html
        const text = temp.textContent || temp.innerText || ''
        return text
          .split('\n')
          .map((line) => `<p>${line}</p>`)
          .join('')
      },
    },
  })

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (editor && isMounted) {
      const currentContent = editor.getHTML()
      if (currentContent !== value) {
        editor.commands.setContent(value || '<p></p>', { emitUpdate: false })
      }
    }
  }, [value, editor, isMounted])

  const ToolbarButton = useCallback(
    ({
      onClick,
      isActive,
      icon: Icon,
      tooltip,
      disabled,
    }: {
      onClick: () => void
      isActive?: boolean
      icon: React.ComponentType<{ className?: string }>
      tooltip: string
      disabled?: boolean
    }) => (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
              'inline-flex items-center justify-center rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'disabled:pointer-events-none disabled:opacity-50',
              isActive
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground'
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    ),
    []
  )

  const handleSetLink = useCallback(() => {
    if (!editor) return
    
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt(t('admin.legalDocuments.editor.linkUrl', 'Enter URL'), previousUrl || '')
    
    if (url === null) return
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor, t])

  const handleExportPDF = useCallback(async () => {
    if (!value || value === '<p></p>') {
      toast.error(t('admin.legalDocuments.editor.exportEmpty', 'Nothing to export'), {
        description: t('admin.legalDocuments.editor.exportEmptyDesc', 'The document is empty. Add some content first.'),
      })
      return
    }
    try {
      await exportDocumentPDF(value, 'legal-document')
      toast.success(t('admin.legalDocuments.editor.exportSuccess', 'Exported successfully'), {
        description: t('admin.legalDocuments.editor.exportPDFSuccess', 'PDF downloaded.'),
      })
    } catch {
      toast.error(t('admin.legalDocuments.editor.exportError', 'Export failed'), {
        description: t('admin.legalDocuments.editor.exportPDFError', 'Could not generate PDF. Try again.'),
      })
    }
  }, [value, t])

  const handleExportDOCX = useCallback(async () => {
    if (!value || value === '<p></p>') {
      toast.error(t('admin.legalDocuments.editor.exportEmpty', 'Nothing to export'), {
        description: t('admin.legalDocuments.editor.exportEmptyDesc', 'The document is empty. Add some content first.'),
      })
      return
    }
    try {
      await exportDocumentDOCX(value, 'legal-document')
      toast.success(t('admin.legalDocuments.editor.exportSuccess', 'Exported successfully'), {
        description: t('admin.legalDocuments.editor.exportDOCXSuccess', 'DOCX downloaded.'),
      })
    } catch {
      toast.error(t('admin.legalDocuments.editor.exportError', 'Export failed'), {
        description: t('admin.legalDocuments.editor.exportDOCXError', 'Could not generate DOCX. Try again.'),
      })
    }
  }, [value, t])

  if (!editor || !isMounted) {
    return (
      <div className={cn('border rounded-md overflow-hidden flex flex-col max-h-[450px]', className)}>
        <div className="flex items-center gap-1 p-2 border-b bg-muted/50 h-10 shrink-0" />
        <div className="flex-1 min-h-[250px] bg-background animate-pulse" />
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn('border rounded-md flex flex-col max-h-[450px]', className)}>
        <div className="flex items-center justify-between gap-1 p-2 border-b bg-muted/50 shrink-0 sticky top-0 z-10">
          <div className="flex items-center gap-0.5 flex-wrap">
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              isActive={editor.isActive('heading', { level: 2 })}
              icon={Heading2}
              tooltip={t('admin.legalDocuments.editor.headline', 'Headline')}
            />
            <div className="w-px h-5 bg-border mx-1" />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
              icon={Bold}
              tooltip={t('admin.legalDocuments.editor.bold', 'Bold')}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
              icon={Italic}
              tooltip={t('admin.legalDocuments.editor.italic', 'Italic')}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive('underline')}
              icon={UnderlineIcon}
              tooltip={t('admin.legalDocuments.editor.underline', 'Underline')}
            />
            <ToolbarButton
              onClick={handleSetLink}
              isActive={editor.isActive('link')}
              icon={LinkIcon}
              tooltip={t('admin.legalDocuments.editor.link', 'Link')}
            />
            <div className="w-px h-5 bg-border mx-1" />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive('bulletList')}
              icon={List}
              tooltip={t('admin.legalDocuments.editor.bulletList', 'Bullet List')}
            />
            <div className="w-px h-5 bg-border mx-1" />
            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              icon={Undo}
              tooltip={t('admin.legalDocuments.editor.undo', 'Undo')}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              icon={Redo}
              tooltip={t('admin.legalDocuments.editor.redo', 'Redo')}
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs"
              >
                <Download className="h-3.5 w-3.5" />
                {t('admin.legalDocuments.editor.export', 'Export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileText className="mr-2 h-4 w-4" />
                {t('admin.legalDocuments.editor.exportPDF', 'Export as PDF')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportDOCX}>
                <FileText className="mr-2 h-4 w-4" />
                {t('admin.legalDocuments.editor.exportDOCX', 'Export as DOCX')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex-1 overflow-y-auto bg-background">
          <EditorContent
            editor={editor}
            className="min-h-[250px]"
            placeholder={placeholder}
          />

          {placeholder && editor.isEmpty && (
            <div className="px-4 py-3 text-sm text-muted-foreground pointer-events-none -mt-[250px]">
              {placeholder}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
